/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { t } from '@lingui/macro'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip } from '@mui/material'
import { CandidateIndexDTO } from '@om1/aspen-api/codegen/client/models/CandidateIndexDTO'
import { CalendarCheck, CalendarDays, DownloadIcon, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useCreateCalendarEvent } from '../hooks/useCreateCalendarEvent'

function formatDownloadDate(isoString: string): string {
    return new Date(isoString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    })
}

function buildTooltip(count: number, lastAt: string | null | undefined, lastBy: string | null | undefined): string {
    if (!count || !lastAt) return 'Download Calendar Invite'
    const date = formatDownloadDate(lastAt)
    const by = lastBy ?? 'unknown'
    if (count === 1) return `Downloaded on ${date} by ${by}`
    return `Downloaded ${count} times — last by ${by} on ${date}`
}

export const DownloadCalendarAction = ({ candidate, onDownloaded }: { candidate: CandidateIndexDTO; onDownloaded?: () => void }) => {
    const [confirmOpen, setConfirmOpen] = useState(false)
    // Google-first create flow is shared with the candidate detail page via
    // ``useCreateCalendarEvent`` so the two stay in sync. This component layers
    // the list-only "already downloaded" confirmation dialog on top.
    const { triggerCreate, loading } = useCreateCalendarEvent({ candidate, onDownloaded })

    const downloadCount = candidate.calendarDownloadCount ?? 0
    const alreadyDownloaded = downloadCount > 0

    const handleClick = () => {
        if (alreadyDownloaded) {
            setConfirmOpen(true)
        } else {
            triggerCreate()
        }
    }

    const tooltipText = buildTooltip(downloadCount, candidate.lastCalendarDownloadAt, candidate.lastCalendarDownloadBy)

    return (
        <>
            <Tooltip title={tooltipText}>
                <button
                    onClick={handleClick}
                    className={`flex items-center justify-center p-1.5 rounded-md transition-colors shadow-sm h-[36px] min-w-[40px] ${
                        alreadyDownloaded
                            ? 'bg-green-50 border border-green-200 text-green-700 hover:bg-green-100'
                            : 'bg-white border border-gray-200 text-primary-main hover:bg-gray-50'
                    }`}
                >
                    {alreadyDownloaded ? <CalendarCheck className='w-4 h-4' /> : <CalendarDays className='w-4 h-4' />}
                </button>
            </Tooltip>

            <Dialog
                open={confirmOpen}
                // Don't allow Esc / backdrop-click to close while the create
                // call is running — otherwise the spinner UI vanishes mid
                // request and the user thinks nothing happened.
                onClose={() => {
                    if (!loading) setConfirmOpen(false)
                }}
                maxWidth='xs'
                fullWidth
            >
                <DialogTitle>{t`Already Downloaded`}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {buildTooltip(downloadCount, candidate.lastCalendarDownloadAt, candidate.lastCalendarDownloadBy)}
                        {'. '}
                        {t`Do you want to download it again?`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} color='inherit' disabled={loading}>
                        {t`Cancel`}
                    </Button>
                    <Button
                        onClick={async () => {
                            // Keep the dialog open while the work runs so the
                            // user actually sees the spinner. ``triggerCreate``
                            // already manages ``loading`` via try/finally,
                            // so we just await it and then close.
                            await triggerCreate()
                            setConfirmOpen(false)
                        }}
                        variant='contained'
                        disabled={loading}
                        startIcon={loading ? <Loader2 className='w-4 h-4 animate-spin' /> : <DownloadIcon className='w-4 h-4' />}
                    >
                        {loading ? t`Creating…` : t`Download Again`}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
